import { useEffect } from "react";
import axiosPrivate from "@api/axiosPrivate";
import { useRefreshToken } from "./useRefreshToken";
import { useSelector } from "react-redux";

const isAuthRoute = (url) =>
  /(?:^|\/)auth\/(?:login|logout|refresh|forgot-password|reset-password|accept-invite|verify-code|resend-verification|company-registration)(?:[?#]|$)/.test(url || "");
const useAxiosPrivate = ({ proactive = false } = {}) => {
  const token = useSelector((state) => state.user.access_token);
  const refresh = useRefreshToken();
  if (token)
    axiosPrivate.defaults.headers.common.Authorization = `Bearer ${token}`;
  else delete axiosPrivate.defaults.headers.common.Authorization;

  // Only the app-level instance owns proactive refresh, avoiding rotating-cookie races.
  useEffect(() => {
    if (!proactive || !token) return;
    let timer;
    try {
      const payload = JSON.parse(
        atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
      );
      if (payload.exp)
        timer = setTimeout(
          () => {
            refresh().catch(() => {});
          },
          Math.max(0, payload.exp * 1000 - Date.now() - 5 * 60 * 1000),
        );
    } catch {
      /* Invalid tokens are handled by the next authenticated request. */
    }
    return () => clearTimeout(timer);
  }, [proactive, token, refresh]);

  useEffect(() => {
    const request = axiosPrivate.interceptors.request.use((config) => {
      if (isAuthRoute(config.url)) delete config.headers.Authorization;
      else if (token && !config.headers.Authorization)
        config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
    const response = axiosPrivate.interceptors.response.use(
      (value) => value,
      async (error) => {
        const config = error?.config;
        // A permission denial (403) must not refresh or sign the user out.
        if (
          token &&
          config &&
          !config.authRetried &&
          !error.authHandled &&
          !isAuthRoute(config.url) &&
          error?.response?.status === 401
        ) {
          error.authHandled = true;
          config.authRetried = true;
          const next = await refresh();
          config.headers.Authorization = `Bearer ${next}`;
          return axiosPrivate(config);
        }
        return Promise.reject(error);
      },
    );
    return () => {
      axiosPrivate.interceptors.request.eject(request);
      axiosPrivate.interceptors.response.eject(response);
    };
  }, [token, refresh]);
  return axiosPrivate;
};
export { useAxiosPrivate };
